import { useLayoutEffect } from 'react'
import { Link } from '@tanstack/react-router'
import { ArrowLeft, ArrowRight, BookOpen } from 'lucide-react'
import { PageShell } from './page-shell'
import { HELP_GROUPS, HELP_PAGES, type HelpPage } from './help-content'
import './help-center.css'

const HELP_SCROLL_POSITION = '__fastcutHelpScrollPosition'

declare global {
  interface Window {
    __fastcutHelpScrollPosition?: number
  }
}

function preserveHelpScroll() {
  window[HELP_SCROLL_POSITION] = window.scrollY
}

export function HelpCenter({ page }: { page?: HelpPage }) {
  useLayoutEffect(() => {
    const scrollTop = window[HELP_SCROLL_POSITION]
    if (typeof scrollTop !== 'number') return

    window.scrollTo({ top: scrollTop })
    const frame = window.requestAnimationFrame(() => {
      window.scrollTo({ top: scrollTop })
    })
    // The router's own reset runs after the route tree commits. Keep the reader
    // at the same place through that brief transition rather than flashing them
    // back to the top of the new article.
    const interval = window.setInterval(() => {
      window.scrollTo({ top: scrollTop })
    }, 16)
    const timer = window.setTimeout(() => {
      window.clearInterval(interval)
      window.scrollTo({ top: scrollTop })
      if (window[HELP_SCROLL_POSITION] === scrollTop) delete window[HELP_SCROLL_POSITION]
    }, 500)
    return () => {
      window.cancelAnimationFrame(frame)
      window.clearInterval(interval)
      window.clearTimeout(timer)
    }
  }, [page?.slug])

  return <PageShell><div className="help-center bg-[#f6f9fc] py-5 text-[#101727] sm:py-6"><div className="mx-auto max-w-[1260px] px-4 sm:px-6"><div className="mb-5 flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-[#e5e7eb] pb-5"><p className="text-xs font-bold tracking-[.12em] text-[#5255f6]">JIAN HELP CENTER</p><h1 className="text-xl font-semibold tracking-[-.03em]">产品使用指南</h1><p className="text-sm text-[#697184]">AI 初稿、人工审核与最终交付说明</p></div><div className="grid gap-8 lg:grid-cols-[310px_minmax(0,1fr)]"><aside className="help-sidebar lg:sticky lg:top-20 lg:h-[calc(100vh-5.5rem)] lg:overflow-y-auto"><HelpNav currentSlug={page?.slug} /></aside><main className="min-w-0">{page ? <HelpArticle page={page} /> : <HelpHome />}</main></div></div></div></PageShell>
}

function HelpNav({ currentSlug }: { currentSlug?: string }) {
  return (
    <nav className="border border-[#e5e7eb] bg-white p-3" aria-label="帮助目录">
      <Link
        to="/help"
        resetScroll={false}
        onMouseDown={preserveHelpScroll}
        onClick={preserveHelpScroll}
        aria-current={!currentSlug ? 'page' : undefined}
        className="help-nav-link flex items-center gap-2 px-3 py-2.5 text-sm font-semibold"
      >
        <BookOpen className="size-4" />帮助概览
      </Link>
      {HELP_GROUPS.map((group) => {
        const pages = HELP_PAGES.filter((page) => page.category === group)
        return pages.length ? (
          <div key={group} className="mt-5">
            <p className="px-3 pb-2 text-[11px] font-bold tracking-[.09em] text-[#8c94a3]">{group}</p>
            {pages.map((page) => (
              <Link
                key={page.slug}
                to="/help/$slug"
                params={{ slug: page.slug }}
                resetScroll={false}
                onMouseDown={preserveHelpScroll}
                onClick={preserveHelpScroll}
                aria-current={currentSlug === page.slug ? 'page' : undefined}
                className="help-nav-link block px-3 py-2 text-sm leading-5"
              >
                {page.title}
              </Link>
            ))}
          </div>
        ) : null
      })}
    </nav>
  )
}
function HelpHome() { return <div className="space-y-8"><section className="border border-[#e5e7eb] bg-white p-7 sm:p-10"><p className="text-xs font-bold tracking-[.12em] text-[#5255f6]">从一个内容源开始</p><h2 className="mt-3 text-3xl font-semibold tracking-[-.045em] sm:text-4xl">AI 先做初稿，你保留最后控制权。</h2><p className="mt-4 max-w-2xl leading-7 text-[#697184]">JIAN 帮你完成切片、字幕、配音和多版本适配；每一条视频都可以回到 VideoCut 时间线检查和精修。</p><Link to="/help/$slug" params={{slug:'ai-smart-clipping'}} className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#5255f6] px-5 py-3 text-sm font-semibold text-white">从智能切片开始 <ArrowRight className="size-4" /></Link></section><div className="grid gap-4 md:grid-cols-2">{HELP_PAGES.slice(0,4).map(p => <HelpCard key={p.slug} page={p} />)}</div></div> }
function HelpCard({ page }: { page: HelpPage }) { return <Link to="/help/$slug" params={{slug:page.slug}} className="group border border-[#e5e7eb] bg-white p-6 transition-colors hover:border-[#5255f6]"><p className="text-xs font-bold text-[#5255f6]">AI 工具与自动化</p><h2 className="mt-3 text-xl font-semibold">{page.title}</h2><p className="mt-2 text-sm leading-6 text-[#697184]">{page.description}</p><span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#5255f6]">查看详情 <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" /></span></Link> }
function HelpArticle({ page }: { page: HelpPage }) { return <article className="border border-[#e5e7eb] bg-white"><header className="border-b border-[#e5e7eb] p-7 sm:p-10"><Link to="/help" className="inline-flex items-center gap-2 text-sm text-[#697184] hover:text-[#5255f6]"><ArrowLeft className="size-4" />帮助中心</Link><p className="mt-7 text-xs font-bold tracking-[.12em] text-[#5255f6]">{page.category}</p><h2 className="mt-3 text-3xl font-semibold tracking-[-.05em] sm:text-4xl">{page.title}</h2><p className="mt-4 leading-7 text-[#697184]">{page.description}</p></header><div className="space-y-10 p-7 sm:p-10">{page.sections.map(section => <section key={section.title}><h3 className="text-xl font-semibold">{section.title}</h3><ul className="mt-4 space-y-3 text-[15px] leading-7 text-[#4d5a6d]">{section.items.map(item => <li key={item} className="flex gap-3"><span className="mt-3 size-1.5 shrink-0 rounded-full bg-[#5255f6]" />{item}</li>)}</ul></section>)}</div></article> }
